import {
  Accordion,
  AccordionItem,
  Checkbox,
  Column,
  Link,
  Row,
  Toggle,
  Tooltip,
} from 'carbon-components-react';
import PublicImage from 'src/common/components/PublicImage';
import '../Assessments/index.scss';
import { useEffect, useState } from 'react';
import { StepProps } from '../../../lib/interfaces';
import { defaultValues, segmentsTrack } from '../../../constants/default';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { ChevronDown16, ChevronUp16 } from '@carbon/icons-react';

type FeatureFlags = {
  [key: string]: boolean;
};

const initialConnectivityFlags = {
  iscsi: true,
  fiberChannel: true,
  nvme: false,
  vmware: false,
  partyPlatforms: true,
};

const initialAdvancedFlags = {
  flashTechnology: true,
  hwCompression: true,
  swDataReduction: false,
  encryption: false,
  transparentData: true,
};

const Step3 = ({ postObj, setPostObj, useTranslation }: StepProps) => {
  const advancedConnectivity =
    defaultValues('advancedConnectivity', useTranslation) || [];
  const advancedItems = defaultValues('advancedItems', useTranslation) || [];
  const connectivityItems =
    defaultValues('connectivityItems', useTranslation) || [];
  const connectivityObj = Object.assign({}, initialConnectivityFlags);
  const advancedObj = Object.assign({}, initialAdvancedFlags);
  const [connectivity, setConnectivity] =
    useState<FeatureFlags>(connectivityObj);
  const [advanced, setAdvanced] = useState<FeatureFlags>(advancedObj);
  const [openIndex, setOpenIndex] = useState(0);
  const [isChecked, setIsChecked] = useState(true);
  const [isCheckedCyber, setIsCheckedCyber] = useState(true);

  const updateAccordian = (bool: boolean, value: number) => {
    if (bool) setOpenIndex(value);
    else setOpenIndex(0);
  };
  const hasChangedCheck = (type: string, stateObj: any) => {
    const defaultObj =
      type === 'business' ? initialConnectivityFlags : initialAdvancedFlags;
    return Object.keys(stateObj).some((key) => {
      return (
        stateObj[key as keyof typeof defaultObj] !==
        defaultObj[key as keyof typeof defaultObj]
      );
    });
  };
  const handleContinuityChange = async (
    key: string,
    value: boolean,
    type: string,
  ) => {
    if (type === 'business') {
      connectivity[key] = value;
      setConnectivity(connectivity);
      await setConnectivity((prevFlags) => {
        const updatedFlags = { ...prevFlags, [key]: value };
        setIsChecked(!hasChangedCheck(type, updatedFlags));
        return updatedFlags;
      });
    }
    if (type === 'cyber') {
      advanced[key] = value;
      setAdvanced(advanced);
      await setAdvanced((prevFlags) => {
        const updatedFlags = { ...prevFlags, [key]: value };
        setIsCheckedCyber(!hasChangedCheck(type, updatedFlags));
        return updatedFlags;
      });
    }
  };
  const onChangeBusiness = (bool: boolean, type: string) => {
    if (type === 'business') {
      setIsChecked(bool);
      if (!!bool)
        setConnectivity((prevFlags) => {
          return { ...prevFlags, ...initialConnectivityFlags };
        });
      else
        setConnectivity((prevFlags) => {
          return {
            ...prevFlags,
            ...initialConnectivityFlags,
            ...{
              iscsi: false,
              fiberChannel: false,
              partyPlatforms: false,
            },
          };
        });
    }
    if (type === 'cyber') {
      setIsCheckedCyber(bool);
      if (!!bool)
        setAdvanced((prevFlags) => {
          return { ...prevFlags, ...initialAdvancedFlags };
        });
      else
        setAdvanced((prevFlags) => {
          return {
            ...prevFlags,
            ...initialAdvancedFlags,
            ...{
              flashTechnology: false,
              hwCompression: false,
              transparentData: false,
            },
          };
        });
    }
    segment('Default Option Selected');
  };
  const _triggerUpdate = () =>
    setPostObj((prevFlags: any) => {
      const updatedFlags = Object.assign({}, prevFlags, connectivity, advanced);
      return updatedFlags;
    });
  const segment = (evtKey = '') => {
    segmentsTrack(evtKey, 'Assessment Step 3 Page');
  };
  useEffect(() => {
    _triggerUpdate();
  }, [connectivity, advanced]);
  useEffect(() => {
    Object.keys(postObj).map(async (key) => {
      if (connectivity.hasOwnProperty(key)) {
        connectivity[key] = postObj[key];
        await setConnectivity((prevFlags) => {
          const updatedFlags = { ...prevFlags, [key]: postObj[key] };
          setIsChecked(!hasChangedCheck('business', updatedFlags));
          return updatedFlags;
        });
      }
      if (advanced.hasOwnProperty(key)) {
        advanced[key] = postObj[key];
        await setAdvanced((prevFlags) => {
          const updatedFlags = { ...prevFlags, [key]: postObj[key] };
          setIsCheckedCyber(!hasChangedCheck('cyber', updatedFlags));
          return updatedFlags;
        });
      }
    });
    _triggerUpdate();
  }, []);
  const [openIndexFirst, setOpenIndexFirst] = useState(true);
  const [openIndexSecond, setOpenIndexSecond] = useState(true);
  const isSmall = useSmallBreakpoint('560px');

  const firstContent: any = advancedConnectivity[1];
  const secondContent: any = advancedConnectivity[2];
  const [expandAll, setExpandAll] = useState(true);
  useEffect(() => {
    if (!!openIndexFirst && !!openIndexSecond) setExpandAll(true);
    if (!openIndexFirst && !openIndexSecond) setExpandAll(false);
  }, [openIndexFirst, openIndexSecond]);
  const _triggerAccordian = (): void => {
    setOpenIndexSecond(!openIndexSecond);
  };

  const _triggerAccordianLink = (): void => {
    setOpenIndexFirst(!expandAll);
    setOpenIndexSecond(!expandAll);
    setExpandAll(!expandAll);
    segment(expandAll === true ? 'Collapse' : 'Expand' + ' All click');
  };

  return (
    <div className="bx--row fixHeight">
      <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-16 bx--col-padding border-lr p-tb-0">
        <table className="fixHeight">
          <tr>
            <td className="table-td-desc slide_text bx--col-padding p-b-0">
              <span>{useTranslation('step3_para')}</span>
            </td>
            {!isSmall && (
              <td rowSpan={openIndexFirst || openIndexSecond ? 1 : 2}>
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 bx--col-padding p-t-a p-b-0">
                  <PublicImage
                    imgPath={'images/connectivity.svg'}
                    widgetId="solution-advisor"
                    width="100%"
                    height="100%"
                    className={
                      openIndexFirst || openIndexSecond
                        ? 'image-fixed screen'
                        : 'screen'
                    }
                    alt="animation-image"
                  />
                </div>
              </td>
            )}
          </tr>
          {isSmall && (
            <tr>
              <td className="h-0">
                <div className="bx--col-md-8 bx--col-sm-4">
                  <PublicImage
                    imgPath={'images/connectivity.svg'}
                    widgetId="solution-advisor"
                    width="100%"
                    height="100%"
                    alt="animation-image"
                    className="screen small-screen"
                  />
                </div>
              </td>
            </tr>
          )}
          <tr>
            <td className={!isSmall ? 'b-r-1' : ''}>
              <Link
                href="javascript:;"
                className="link-accordian"
                onClick={() => _triggerAccordianLink()}
              >
                {!!expandAll
                  ? useTranslation('step2_collapse_all')
                  : useTranslation('step2_expand_all')}
                {!!expandAll ? (
                  <ChevronUp16 className="m-l-6" />
                ) : (
                  <ChevronDown16 className="m-l-6" />
                )}
              </Link>
              <Accordion
                size="sm"
                className="interactive-widget__accordion p-t-accordian float-lg-start"
              >
                <AccordionItem
                  title={useTranslation('step3_accordion_title1')}
                  onHeadingClick={(data) =>
                    setOpenIndexFirst(openIndexFirst === true ? false : true)
                  }
                  open={openIndexFirst}
                  className="p-b-0"
                >
                  <div className="b-b-1">
                    {connectivityItems.map((business: any, index) => (
                      <Row key={index}>
                        <Column sm={3} md={6} lg={12} className="align-hcenter">
                          <span>{business.text}</span>
                        </Column>
                        <Column sm={1} md={2} lg={4} className="p-l-0 p-lr-0">
                          {isSmall && (
                            <div className="tooltip-div">
                              <Tooltip
                                autoOrientation={true}
                                direction="right"
                                tabIndex={0}
                              >
                                <span className="slide_text">
                                  {firstContent.lists[index]}
                                </span>
                              </Tooltip>
                            </div>
                          )}
                          <div
                            style={{
                              float: 'right',
                              width: !isSmall ? '100%' : '70%',
                            }}
                          >
                            <Toggle
                              labelA={
                                useTranslation('step_option_no') as string
                              }
                              labelB={
                                useTranslation('step_option_yes') as string
                              }
                              id={business.value}
                              toggled={
                                connectivity[
                                  business.value as keyof typeof initialConnectivityFlags
                                ]
                              }
                              size="sm"
                              onChange={() => {
                                handleContinuityChange(
                                  business.value,
                                  !connectivity[business.value],
                                  'business',
                                );
                                segment(`${business.text} Selected`);
                              }}
                            />
                          </div>
                        </Column>
                      </Row>
                    ))}
                  </div>
                  {isSmall && (
                    <div className="bx--col-padding disclaimer-height b-b-1">
                      {openIndexFirst &&
                        useTranslation('step3_right_desclaim1')}
                    </div>
                  )}
                  <div className="bx--col-padding disclaimer-height product-desc">
                    <Checkbox
                      id="checkbox_business"
                      labelText={useTranslation('step_recommendation')}
                      checked={isChecked}
                      onChange={() => onChangeBusiness(!isChecked, 'business')}
                    />
                  </div>
                </AccordionItem>
                <AccordionItem
                  title={useTranslation('step3_accordion_title2')}
                  onHeadingClick={(data) => _triggerAccordian()}
                  open={openIndexSecond}
                  className="p-b-0"
                >
                  <div className="b-b-1">
                    {advancedItems.map((cyber: any, index) => (
                      <Row key={index}>
                        <Column sm={3} md={6} lg={12} className="align-hcenter">
                          <span>{cyber.text}</span>
                        </Column>
                        <Column sm={1} md={2} lg={4} className="p-l-0 p-lr-0">
                          {isSmall && (
                            <div className="tooltip-div">
                              <Tooltip
                                autoOrientation={true}
                                direction="right"
                                tabIndex={0}
                              >
                                <span className="slide_text">
                                  {secondContent.lists[index]}
                                </span>
                              </Tooltip>
                            </div>
                          )}
                          <div
                            style={{
                              float: 'right',
                              width: !isSmall ? '100%' : '70%',
                            }}
                          >
                            <Toggle
                              labelA={
                                useTranslation('step_option_no') as string
                              }
                              labelB={
                                useTranslation('step_option_yes') as string
                              }
                              id={cyber.value}
                              toggled={
                                advanced[
                                  cyber.value as keyof typeof initialAdvancedFlags
                                ]
                              }
                              size="sm"
                              onChange={() => {
                                handleContinuityChange(
                                  cyber.value,
                                  !advanced[cyber.value],
                                  'cyber',
                                );
                                segment(`${cyber.text} Selected`);
                              }}
                            />
                          </div>
                        </Column>
                      </Row>
                    ))}
                  </div>
                  {isSmall && (
                    <div className="bx--col-padding disclaimer-height b-b-1">
                      {openIndexSecond &&
                        useTranslation('step3_right_desclaim2')}
                    </div>
                  )}
                  <div className="bx--col-padding disclaimer-height product-desc">
                    <Checkbox
                      id="checkbox"
                      labelText={useTranslation('step_recommendation')}
                      checked={isCheckedCyber}
                      onChange={() =>
                        onChangeBusiness(!isCheckedCyber, 'cyber')
                      }
                    />
                  </div>
                </AccordionItem>
              </Accordion>
            </td>
            {(openIndexFirst || openIndexSecond) && !isSmall && (
              <td className="b-r-1">
                <Link
                  href="javascript:;"
                  className="link-accordian hide-accordian"
                >
                  {expandAll === true ? 'Collapse All' : 'Expand All'}
                </Link>
                <Accordion
                  size="sm"
                  className="interactive-widget__accordion p-t-accordian float-lg-start"
                >
                  <AccordionItem
                    title={useTranslation('step3_accordion_title1')}
                    className={
                      openIndexFirst ? 'hide-content' : 'hide-accordian'
                    }
                    open={openIndexFirst}
                  >
                    <div className="b-b-1">
                      {firstContent.lists.map((list: string, key: number) => (
                        <Row key={key}>
                          <Column
                            sm={16}
                            md={8}
                            lg={16}
                            className="align-hcenter"
                          >
                            <span>{list}</span>
                          </Column>
                        </Row>
                      ))}
                    </div>
                    <div className="bx--col-padding disclaimer-height product-desc">
                      {useTranslation('step3_right_desclaim1')}
                    </div>
                  </AccordionItem>
                  <AccordionItem
                    title={useTranslation('step3_accordion_title2')}
                    onHeadingClick={(data) => updateAccordian(data.isOpen, 2)}
                    open={openIndexSecond}
                    className={
                      openIndexSecond ? 'hide-content' : 'hide-accordian'
                    }
                  >
                    <div className="b-b-1">
                      {secondContent.lists.map((list: string, key: number) => (
                        <Row key={key}>
                          <Column
                            sm={16}
                            md={8}
                            lg={16}
                            className="align-hcenter"
                          >
                            <span>{list}</span>
                          </Column>
                        </Row>
                      ))}
                    </div>
                    <div className="bx--col-padding disclaimer-height product-desc">
                      {useTranslation('step3_right_desclaim2')}
                    </div>
                  </AccordionItem>
                </Accordion>
              </td>
            )}
          </tr>
        </table>
      </div>
    </div>
  );
};

export default Step3;
